import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Compass,
  Award,
  Star,
  Quote,
} from 'lucide-react';
import BlockReveal from '../components/BlockReveal';
import ParticleNetwork from '../components/animations/ParticleNetwork';
import { mainServices, SERVICE_IMAGES, HERO_IMAGES } from '../data/serviceConfig';
import SEO from '../components/SEO';
import TypeWriter from '../components/TypeWriter';
import SoftwareWeUse from '../components/SoftwareWeUse';
import {
  homeBenefits as benefits,
  homeTestimonials as testimonials,
  homeStats as stats,
  homeKeyFeatures as keyFeatures,
  homeIndustries as industries,
  homeProcessSteps as processSteps,
} from '../data/homeContent';

const Home = () => {
  return (
    <div className="overflow-hidden">
      <SEO
        title="Land Survey CAD Drafting & Outsourcing Services | Land Drafters"
        description="Outsourced land survey CAD drafting for surveying firms across the USA. Get support for boundary, topographic, ALTA/NSPS, Civil 3D, LiDAR and GIS projects."
        keywords="land survey CAD drafting services provider, outsourced survey CAD drafting, land survey CAD drafting services, survey CAD drafting company, survey drafting outsourcing, outsourced CAD services for surveyors, CAD drafting for land surveyors, land surveying drafting services, Civil 3D survey drafting, GIS mapping services, LiDAR to CAD, survey data conversion, ALTA/NSPS survey drafting, boundary survey drafting, topographic survey drafting, as-built survey drafting"
        path="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] bg-[#0A0A0A] text-white flex items-center" data-testid="hero-section">
        <ParticleNetwork color="#02d871" lineColor="#02d871" particleCount={90} maxDistance={140} speed={0.35} />
        <div className="container-custom relative z-10 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <BlockReveal color="#02d871" delay={200} duration={800}>
                <span className="inline-block bg-primary/20 text-primary px-4 py-2 text-xs font-bold uppercase tracking-widest mb-6">
                  Outsourced Survey CAD Drafting
                </span>
              </BlockReveal>
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black uppercase tracking-tight leading-none mb-6">
                <TypeWriter
                  texts={[
                    [
                      {text:'Your Survey ', cls:''},
                      {text:'Drafting Backlog', cls:'text-primary italic'},
                      {text:'. ', cls:''},
                      {text:'Our ', cls:''},
                      {text:'CAD Production', cls:'text-secondary italic font-light'},
                      {text:' Support.', cls:''},
                    ],
                    [
                      {text:'Scale Your ', cls:''},
                      {text:'Survey Firm', cls:'text-secondary italic'},
                      {text:' With ', cls:''},
                      {text:'Reliable Drafting', cls:'text-primary'},
                    ],
                    [
                      {text:'From Field Data to ', cls:''},
                      {text:'Sealed-Ready CAD', cls:'text-primary italic'},
                      {text:' in 24–48 Hours', cls:''},
                    ],
                  ]}
                  speed={55}
                  deleteSpeed={28}
                  pauseTime={3000}
                />
              </h1>
              <BlockReveal color="#ffaf02" delay={600} duration={900}>
                <p className="hero-tagline text-lg md:text-xl text-white/70 mb-6 max-w-xl">
                  Land Drafters helps US land surveying companies manage overflow with reliable survey CAD drafting, Civil 3D production, GIS mapping, and survey data conversion — all delivered to your firm&apos;s standards.
                </p>
              </BlockReveal>
              <div className="flex flex-wrap gap-2 mb-7" data-testid="hero-chips">
                {['Land Survey CAD Drafting', 'Civil 3D', 'GIS Mapping', 'LiDAR to CAD', 'Outsourced Production'].map((c) => (
                  <span key={c} className="inline-flex items-center px-3 py-1.5 bg-white/5 border border-white/15 text-white/85 text-xs font-semibold rounded-full">{c}</span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/get-quote"
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="hero-cta-quote"
                >
                  Request a Quote
                  <ArrowRight size={16} />
                </Link>
                <Link
                  to="/survey-cad-outsourcing"
                  className="border-2 border-white/40 text-white hover:bg-white hover:text-black uppercase tracking-widest font-bold px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                  data-testid="hero-cta-services"
                >
                  Discuss Your Drafting Needs
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <img 
                  src={HERO_IMAGES.home}
                  alt="Land survey CAD drafting services — survey drawing on a drafting table"
                  className="w-full aspect-square object-cover border-4 border-white/10"
                 width="1200" height="800" fetchPriority="high" decoding="async" />
                {/* Floating Stats Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="absolute -bottom-8 -left-8 bg-white text-black p-6 shadow-2xl"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center">
                      <CheckCircle2 className="text-white" size={28} />
                    </div>
                    <div>
                      <p className="text-3xl font-black">15K+</p>
                      <p className="text-sm text-muted-foreground uppercase tracking-wide">Projects Delivered</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

      </section>

      {/* Stats Bar */}
      <section className="bg-primary py-8" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center text-white"
              >
                <p className="text-3xl md:text-4xl font-black">{stat.number}</p>
                <p className="text-sm uppercase tracking-widest text-white/80">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-spacing grid-pattern" data-testid="about-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-primary font-bold uppercase tracking-widest text-sm">About Us</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase mt-4 mb-6">
                <TypeWriter texts={[
                  [{text:'Trusted ', cls:''}, {text:'Experts', cls:'text-primary italic'}, {text:' in CAD Drafting Services', cls:''}],
                  [{text:'Your Reliable ', cls:''}, {text:'CAD Drafting', cls:'text-secondary italic'}, {text:' Partner', cls:''}],
                ]} speed={50} deleteSpeed={25} pauseTime={3000} />
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Land Drafters LLC dedicates itself to delivering engineering CAD drafting services which fulfill all industry benchmarks. Through many years of work experience, our team including skilled CAD drafting contractors delivers projects that combine excellence with precision and satisfied clients across various industries.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 flex items-center justify-center shrink-0">
                    <Award className="text-primary" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Expertise</h3>
                    <p className="text-sm text-muted-foreground">Built on accuracy, efficiency, and cutting-edge technology.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-secondary/10 flex items-center justify-center shrink-0">
                    <Compass className="text-secondary" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Mission</h3>
                    <p className="text-sm text-muted-foreground">Delivering cost-effective, reliable, and customized CAD solutions.</p>
                  </div>
                </div>
              </div>

              <Link 
                to="/about/who-we-are" 
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="about-learn-more"
              >
                Learn More
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src={HERO_IMAGES.serviceDetail}
                alt="Land Surveying Equipment"
                loading="lazy"
                className="w-full h-auto technical-border"
               width="1200" height="800" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-spacing bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="services-section">
        <div className="absolute inset-0 grid-pattern-dark" />
        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.span initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-primary font-bold uppercase tracking-widest text-sm">Our Services</motion.span>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl lg:text-5xl font-black uppercase mt-4 mb-6">
              <TypeWriter texts={[
                [{text:'CAD Drafting ', cls:''}, {text:'Solutions', cls:'text-secondary italic'}],
                [{text:'Professional ', cls:''}, {text:'Design', cls:'text-primary italic'}, {text:' Services', cls:''}],
              ]} speed={55} deleteSpeed={30} pauseTime={2500} />
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="text-white/60">
              Fast, simple, and convenient CAD design services tailored to your project requirements.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0">
            {mainServices.map((service, index) => (
              <motion.div
                key={service.key}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06, duration: 0.5 }}
              >
                <Link 
                  to={service.href}
                  className="card-line-hover block p-8 border border-white/10 hover:bg-white/5 hover:border-primary/40 transition-all duration-300 group h-full relative overflow-hidden"
                  data-testid={`service-card-${service.key}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative z-10">
                    {SERVICE_IMAGES[service.key] && (
                      <div className="w-full h-32 mb-4 overflow-hidden -mx-8 -mt-8 px-0" style={{width:'calc(100% + 4rem)'}}>
                        <img src={SERVICE_IMAGES[service.key]} alt={service.shortTitle} loading="lazy" className="w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity duration-300"  width="1200" height="800" />
                      </div>
                    )}
                    <div className={`w-12 h-12 ${index % 2 === 0 ? 'bg-primary' : 'bg-secondary'} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                      <service.icon className="text-white" size={24} />
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-white group-hover:text-primary transition-colors duration-200">
                      {service.shortTitle}
                    </h3>
                    <p className="text-white/50 text-sm mb-4 leading-relaxed line-clamp-2">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center text-primary font-bold text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                      Explore
                      <ArrowRight size={12} className="ml-1.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-spacing grid-pattern" data-testid="why-us-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-primary font-bold uppercase tracking-widest text-sm">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase mt-4 mb-6">
              <TypeWriter texts={[
                [{text:'Why Our CAD ', cls:''}, {text:'Solutions', cls:'text-primary italic'}, {text:' Stand Out', cls:''}],
                [{text:'Excellence', cls:'text-secondary italic'}, {text:' in Every ', cls:''}, {text:'Draft', cls:'text-primary italic'}],
              ]} speed={50} deleteSpeed={25} pauseTime={3000} />
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="text-primary" size={32} />
                </div>
                <h3 className="font-bold text-lg mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features - 6 differentiators */}
      <section className="section-spacing bg-[#f8faf8] relative" data-testid="key-features-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-primary font-bold uppercase tracking-widest text-xs">Key Features</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase mt-3 mb-4">
              <TypeWriter texts={[
                [{text:'Six Reasons We ', cls:''}, {text:'Stand Apart', cls:'text-primary italic'}],
                [{text:'Built for ', cls:''}, {text:'Precision', cls:'text-secondary italic'}, {text:' at Scale', cls:''}],
              ]} speed={50} deleteSpeed={25} pauseTime={3000} />
            </h2>
            <p className="text-muted-foreground">The capabilities that make Land Drafters the preferred CAD & survey partner for 150+ firms.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {keyFeatures.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.08 }}
                className="bg-white p-7 border border-border hover:border-primary hover:shadow-xl transition-all duration-300 group"
                data-testid={`home-key-feature-${i}`}
              >
                <div className={`w-14 h-14 ${f.color === 'primary' ? 'bg-primary/10 group-hover:bg-primary' : 'bg-secondary/10 group-hover:bg-secondary'} flex items-center justify-center mb-5 transition-colors`}>
                  <f.icon className={`${f.color === 'primary' ? 'text-primary' : 'text-secondary'} group-hover:text-white transition-colors`} size={26} />
                </div>
                <h3 className="font-bold uppercase text-base mb-2 tracking-tight">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="section-spacing bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="process-section">
        <div className="absolute inset-0 grid-pattern-dark opacity-50" />
        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-primary font-bold uppercase tracking-widest text-xs">How We Work</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase mt-3 mb-4">
              <TypeWriter texts={[
                [{text:'From Brief to ', cls:''}, {text:'Delivery', cls:'text-secondary italic'}],
                [{text:'A ', cls:''}, {text:'5-Step', cls:'text-primary italic'}, {text:' Engagement Process', cls:''}],
              ]} speed={50} deleteSpeed={25} pauseTime={3000} />
            </h2>
            <p className="text-white/60">Predictable, transparent, on-schedule — every single project.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {processSteps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative bg-white/5 border border-white/10 p-5 hover:border-primary transition-colors group"
                data-testid={`home-process-${i}`}
              >
                <span className="text-4xl font-black text-primary/30 absolute top-3 right-4 group-hover:text-primary/60 transition-colors">{step.num}</span>
                <div className="w-11 h-11 bg-primary flex items-center justify-center mb-4">
                  <step.icon className="text-white" size={20} />
                </div>
                <h3 className="font-bold uppercase text-sm mb-2 tracking-wide">{step.title}</h3>
                <p className="text-xs text-white/60 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="section-spacing grid-pattern" data-testid="industries-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-primary font-bold uppercase tracking-widest text-xs">Industries Served</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase mt-3 mb-4">
              <TypeWriter texts={[
                [{text:'Trusted Across ', cls:''}, {text:'8 Industries', cls:'text-primary italic'}],
                [{text:'Built for ', cls:''}, {text:'Every Sector', cls:'text-secondary italic'}],
              ]} speed={50} deleteSpeed={25} pauseTime={3000} />
            </h2>
            <p className="text-muted-foreground">Domain depth across construction, infrastructure, real estate, environmental, and more.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {industries.map((ind, i) => (
              <motion.div
                key={ind.title}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 4) * 0.05 }}
                className="bg-white border border-border p-5 text-center hover:border-secondary hover:-translate-y-1 transition-all duration-300 group"
                data-testid={`home-industry-${i}`}
              >
                <div className="w-12 h-12 bg-secondary/10 flex items-center justify-center mb-3 mx-auto group-hover:bg-secondary transition-colors">
                  <ind.icon className="text-secondary group-hover:text-white transition-colors" size={22} />
                </div>
                <h3 className="font-bold text-sm mb-1">{ind.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{ind.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-spacing bg-[#0A0A0A] text-white grid-pattern-dark" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-primary font-bold uppercase tracking-widest text-sm">Testimonials</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase mt-4 mb-6">
              <TypeWriter texts={[
                [{text:'What Our ', cls:''}, {text:'Clients', cls:'text-secondary italic'}, {text:' Say', cls:''}],
                [{text:'Trusted by ', cls:''}, {text:'Industry', cls:'text-primary italic'}, {text:' Leaders', cls:''}],
              ]} speed={55} deleteSpeed={30} pauseTime={3000} />
            </h2>
            <p className="text-white/70">
              We get so many thank you emails and notes every day.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 border border-white/10 p-8 relative"
              >
                <Quote className="text-primary/30 absolute top-4 right-4" size={40} />
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-secondary fill-secondary" size={16} />
                  ))}
                </div>
                <p className="text-white/80 italic mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-bold">{testimonial.author}</p>
                  <p className="text-sm text-white/50">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Software Section */}
      <SoftwareWeUse variant="light" />

      {/* CTA Section */}
      <section className="section-spacing bg-secondary text-white relative overflow-hidden" data-testid="cta-section">
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl lg:text-5xl font-black uppercase mb-6"
            >
              <TypeWriter texts={[
                [{text:'Start Your CAD Project ', cls:''}, {text:'Today!', cls:'italic font-light text-green-300'}],
                [{text:'Get a ', cls:''}, {text:'Free Quote', cls:'text-orange-400 italic'}, {text:' Now!', cls:''}],
              ]} speed={55} deleteSpeed={30} pauseTime={2500} />
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg mb-8 text-white/80"
            >
              Let's create precise, professional drafts that bring your ideas to life.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link 
                to="/get-quote" 
                className="bg-white text-secondary hover:bg-white/90 uppercase tracking-widest font-bold px-8 py-4 text-sm transition-all inline-flex items-center justify-center gap-2"
                data-testid="cta-get-quote"
              >
                Get a Quote
                <ArrowRight size={16} />
              </Link>
              <Link 
                to="/contact" 
                className="border-2 border-white text-white hover:bg-white hover:text-secondary uppercase tracking-widest font-bold px-8 py-4 text-sm transition-all inline-flex items-center justify-center gap-2"
                data-testid="cta-contact"
              >
                Contact Us
              </Link>
            </motion.div>
          </div>
        </div>
        {/* Decorative */}
        <div className="absolute -top-20 -right-20 w-80 h-80 border border-white/10 rounded-full" />
        <div className="absolute -bottom-20 -left-20 w-60 h-60 border border-white/10 rounded-full" />
      </section>
    </div>
  );
};

export default Home;
